import { ArrowRight, UserPlus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import styles from './PeopleSnapshot.module.css'
import BlankState from '../BlankState/BlankState'
import { fmtInt, timeAgo } from '../../../utils/moneyFormat'
import { avatarTone, initials } from '../../../utils/avatar'

// Live people counts plus the most recently registered customers.
// Subscribers are intentionally not shown: the table holds no rows, so the
// figure was only ever going to read zero. Repeat customers (more than one
// booking) is the more useful third metric, per spec §1.0.1.6.
const FIGURES = [
  { key: 'activeCustomers', label: 'Customers' },
  { key: 'travellers', label: 'Travellers' },
  { key: 'repeatCustomers', label: 'Repeat' },
]

const PeopleSnapshot = ({ people, loading }) => {
  const navigate = useNavigate()

  const recent = people?.recent || []
  const newThisWeek = Number(people?.newThisWeek) || 0

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div className={styles.headText}>
          <h3 className="dshTitle">People</h3>
          <p className="dshSub">Customers &amp; travellers</p>
        </div>
        <button
          type="button"
          className="dshAction"
          onClick={() => navigate('/people/customers/private')}
        >
          View all
          <ArrowRight size={13} strokeWidth={2.2} />
        </button>
      </div>

      <div className={`dshBody ${styles.body}`}>
        <div className={styles.figures}>
          {FIGURES.map((f) => (
            <div key={f.key} className={styles.figure}>
              <span className="dshEyebrow">{f.label}</span>

              {loading ? (
                <span className={`dshSkel ${styles.skelFigure}`} />
              ) : (
                <span className={`dshNum ${styles.figureValue}`}>
                  {fmtInt(people?.[f.key] ?? 0)}
                </span>
              )}

              <span className={styles.figureMeta}>
                {!loading && f.key === 'activeCustomers' && newThisWeek > 0 && (
                  <em className="dshPill dshPillPos dshNum">
                    +{fmtInt(newThisWeek)} this week
                  </em>
                )}
              </span>
            </div>
          ))}
        </div>

        <span className={`dshEyebrow ${styles.sectionLabel}`}>Recently joined</span>

        <div className={styles.list}>
          {loading &&
            [0, 1, 2].map((i) => (
              <div key={i} className="dshRow">
                <div className={styles.person}>
                  <span className={`dshSkel ${styles.skelAvatar}`} />
                  <div className={styles.personText}>
                    <span className={`dshSkel ${styles.skelName}`} />
                    <span className={`dshSkel ${styles.skelMeta}`} />
                  </div>
                </div>
                <span className={`dshSkel ${styles.skelPill}`} />
              </div>
            ))}

          {!loading && recent.length === 0 && (
            <BlankState
              icon={UserPlus}
              title="No customers yet"
              text="Newly registered customers show up here with the date they joined."
            />
          )}

          {!loading &&
            recent.map((c) => (
              <div key={`${c.kind}-${c.id}`} className="dshRow">
                <div className={styles.person}>
                  <span
                    className={`dshAvatar dshTone${avatarTone(c.name)} ${styles.avatar}`}
                  >
                    {initials(c.name)}
                  </span>
                  <div className={styles.personText}>
                    <span className={`dshTrunc ${styles.name}`}>{c.name}</span>
                    <span className={styles.meta}>Joined {timeAgo(c.createdAt)}</span>
                  </div>
                </div>

                <div className={styles.status}>
                  <span className="dshPill dshPillMute">{c.kind}</span>
                  <span
                    className={`${styles.dot} ${
                      c.status === 'active' ? styles.dotActive : styles.dotInactive
                    }`}
                    title={c.status === 'active' ? 'Active' : 'Inactive'}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default PeopleSnapshot
